import React from "react";
import { cn } from "@/utils/stylesUtils";
import { PinIcon, ChevronRight } from "lucide-react";
import { joinWithAnd } from "@/utils/helperFunctions";
import isEqual from "lodash.isequal";

const selectedStyles = {
  red: "border-l-4 border-[#C53030] ring-2 ring-[#C53030] ring-offset-2 ring-offset-white my-2 rounded",
  yellow:
    "border-l-4 border-[#E09C5C] ring-2 ring-[#E09C5C] ring-offset-2 ring-offset-white my-2 rounded",
  gray: "border-l-4 border-[#94A3B8] ring-2 ring-[#94A3B8] ring-offset-2 ring-offset-white my-2 rounded",
};

const StudentCard = ({
  student,
  isSelected,
  hovered,
  onClick,
  onHover,
  togglePin,
  isModal,
  chosenClass,
  formattedEmotions,
  isModalOpen,
  isLast = false,
}) => {
  const {
    email,
    name,
    border,
    bg,
    flag,
    level,
    lastEmotion,
    timeAgo,
    percent,
    isPinned,
    latestAlert,
    latestAlertStat,
  } = student;

  const alertTextColor =
    flag === "red"
      ? "text-[#C53030]"
      : flag === "yellow"
        ? "text-[#E09C5C]"
        : "text-[#000000]";

  const tierNumber =
    typeof level === "number"
      ? level
      : flag === "red"
        ? 3
        : flag === "yellow"
          ? 2
          : 1;

  return (
    <div className={cn("h-full", isModalOpen && !isModal && "sm:px-3")}>
      <div
        onClick={() => onClick(email)}
        onMouseEnter={() => onHover(email)}
        onMouseLeave={() => onHover(null)}
        data-onboarding-id="student-supports-student-card"
        className={cn(
          "@container relative cursor-pointer transition-all duration-300 w-full",
          "h-full",
          "border-r border-t border-[#DDDDDD]",
          "px-3 py-2 sm:py-3 2xl:pl-6 2xl:pr-3 flex flex-row items-center justify-between gap-2 sm:gap-3",
          border,
          bg,
          !isModal && isLast && "rounded-b-lg border-b",
          !isModal && isSelected && selectedStyles[flag],
          isSelected && "relative z-10",
          isModalOpen && isSelected && "border-b",
          !isSelected && "hover:shadow-md focus:outline-none",
        )}
        tabIndex={0}
        role="button"
        aria-label={`Student: ${name}`}
      >
        {isModalOpen ? (
          <div className="grid grid-cols-1 2xl:grid-cols-2 items-start gap-2 sm:gap-4 w-full min-w-0">
            {/* left: student */}
            <div className="w-full min-w-0 flex flex-col gap-1">
              <div className="flex gap-2 items-center">
                <span
                  className={cn(
                    "text-sm sm:text-base font-bold flex-shrink-0",
                    isSelected && "text-lg xl:text-[20px] 2xl:text-[22px]",
                    !isModal && "max-w-[200px] 2xl:max-w-[150px] truncate",
                  )}
                >
                  {name}
                </span>
                {(isPinned || hovered === email) && (
                  <PinIcon
                    className={cn(
                      "w-3 h-3 sm:w-4 sm:h-4 shrink-0 cursor-pointer",
                      isPinned ? "fill-black" : "text-gray-400",
                    )}
                    onClick={(e) => togglePin(email, e)}
                    title={isPinned ? "Unpin student" : "Pin student"}
                  />
                )}
              </div>
              <div className="text-sm sm:text-base whitespace-normal break-words leading-snug">
                <span className="font-bold">Last:</span>{" "}
                {lastEmotion ? (
                  <>
                    <span className="capitalize">{lastEmotion}</span>
                    <span className="text-gray-500 ml-1">({timeAgo} ago)</span>
                  </>
                ) : (
                  <span className="text-gray-400">--</span>
                )}
              </div>
              <div className="text-sm sm:text-base text-gray-500">
                {chosenClass}
              </div>
            </div>

            {/* right: alert + pattern */}
            <div className="w-full min-w-0 flex flex-col gap-1">
              <div className="text-sm sm:text-base">
                {latestAlert?.type?.kind === "word" && (
                  <div className="whitespace-normal break-words leading-snug">
                    <span className="font-bold">
                      Tier {tierNumber}{" "}
                      <span className="capitalize">
                        {latestAlert?.type?.kind}
                      </span>{" "}
                      Alert:
                    </span>{" "}
                    <span className={cn("font-normal", alertTextColor)}>
                      Used the{" "}
                      {Array.isArray(latestAlert?.type?.value) &&
                      latestAlert.type.value.length > 1
                        ? "words"
                        : "word"}{" "}
                      "
                      {Array.isArray(latestAlert?.type?.value)
                        ? joinWithAnd(latestAlert?.type?.value)
                        : latestAlert?.type?.value}
                      "
                    </span>
                  </div>
                )}
                {latestAlert?.type?.kind === "emotion" && (
                  <div className="whitespace-normal break-words leading-snug">
                    <span className="font-bold">
                      Tier {tierNumber}{" "}
                      <span className="capitalize">
                        {latestAlert?.type?.kind}
                      </span>{" "}
                      Alert:
                    </span>{" "}
                    <span className={cn("font-normal", alertTextColor)}>
                      Felt {latestAlert?.wellness?.emotion}
                      {latestAlertStat?.percent !== undefined && (
                        <>
                          {" "}
                          ({Number((latestAlertStat?.percent || 0).toFixed(1))}
                          %)
                        </>
                      )}
                    </span>
                  </div>
                )}
              </div>

              <div className="text-sm sm:text-base whitespace-normal break-words leading-snug">
                Felt{" "}
                {percent > 0 ? (
                  <>
                    <span className="lowercase">{formattedEmotions}</span>
                    <span className="ml-1 font-bold">{percent}%</span> of time
                  </>
                ) : (
                  <span className="text-gray-400">--</span>
                )}
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 items-start gap-2 sm:gap-3 w-full min-w-0">
              {/* grid 1: name */}
              <div className="col-span-1 w-full @container">
                <div className="flex gap-2 items-center">
                  <span
                    className={cn(
                      "text-sm sm:text-base font-bold max-w-[80px] lg:max-w-full truncate flex-shrink-0",
                    )}
                  >
                    {name}
                  </span>
                  {(isPinned || hovered === email) && (
                    <PinIcon
                      className={cn(
                        "w-3 h-3 sm:w-4 sm:h-4 shrink-0 cursor-pointer",
                        isPinned ? "fill-black" : "text-gray-400",
                      )}
                      onClick={(e) => togglePin(email, e)}
                      title={isPinned ? "Unpin student" : "Pin student"}
                    />
                  )}
                </div>
                <div className="text-sm sm:text-base text-gray-500">
                  {chosenClass}
                </div>
              </div>

              {/* grid 2: latest alert */}
              <div className="col-span-1 w-full min-w-0">
                {latestAlert?.type?.kind === "word" && (
                  <div className="text-sm sm:text-base">
                    <div className="font-bold">
                      Tier {tierNumber}{" "}
                      <span className="capitalize">
                        {latestAlert?.type?.kind}
                      </span>{" "}
                      Alert
                    </div>
                    <div
                      className={cn(
                        "font-normal whitespace-normal break-words leading-snug",
                        alertTextColor,
                      )}
                    >
                      Used the{" "}
                      {Array.isArray(latestAlert?.type?.value) &&
                      latestAlert.type.value.length > 1
                        ? "words"
                        : "word"}{" "}
                      "
                      {Array.isArray(latestAlert?.type?.value)
                        ? joinWithAnd(latestAlert?.type?.value)
                        : latestAlert?.type?.value}
                      "
                    </div>
                  </div>
                )}
                {latestAlert?.type?.kind === "emotion" && (
                  <div className="text-sm sm:text-base">
                    <div className="font-bold">
                      Tier {tierNumber}{" "}
                      <span className="capitalize">
                        {latestAlert?.type?.kind}
                      </span>{" "}
                      Alert
                    </div>
                    <div
                      className={cn(
                        "font-normal whitespace-normal break-words leading-snug",
                        alertTextColor,
                      )}
                    >
                      Felt {latestAlert?.wellness?.emotion}
                      {latestAlertStat?.percent !== undefined && (
                        <>
                          {" "}
                          ({Number((latestAlertStat?.percent || 0).toFixed(1))}
                          %)
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* grid 3: last emotion */}
              <div className="col-span-1 w-full min-w-0 text-sm sm:text-base">
                <div className="whitespace-normal break-words leading-snug">
                  <span className="font-bold">Last:</span>{" "}
                  {lastEmotion ? (
                    <>
                      <span className="capitalize">{lastEmotion}</span>
                      <span className="text-gray-500 ml-1">
                        ({timeAgo} ago)
                      </span>
                    </>
                  ) : (
                    <span className="text-gray-400">--</span>
                  )}
                </div>
              </div>

              {/* grid 4: emotion pattern */}
              <div className="col-span-1 w-full min-w-0 text-sm sm:text-base">
                <div className="whitespace-normal break-words leading-snug">
                  Felt{" "}
                  {percent > 0 ? (
                    <>
                      <span className="lowercase">{formattedEmotions}</span>
                      <span className="ml-1 font-bold">{percent}%</span> of time
                    </>
                  ) : (
                    <span className="text-gray-400">--</span>
                  )}
                </div>
              </div>
            </div>
            <ChevronRight className="w-4 h-4 sm:w-6 sm:h-6 text-gray-400 flex-shrink-0" />
          </>
        )}
      </div>
    </div>
  );
};

export default React.memo(StudentCard, (prevProps, nextProps) =>
  isEqual(prevProps, nextProps),
);
